import { useEffect, useRef } from "react";
import baseURL from "../components/authComponents/baseURL/BaseURL";

const SESSION_KEY = "sp_visitor_session";
const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const API_BASE = baseURL.defaults.baseURL;

function getSessionToken() {
  // Use sessionStorage: persists across refreshes within the same tab,
  // but is automatically cleared when the tab/window is closed.
  let token = sessionStorage.getItem(SESSION_KEY);
  if (!token) {
    token = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_KEY, token);
  }
  return token;
}

/**
 * Send goodbye beacon to immediately remove this visitor session.
 * Called ONLY on explicit logout — NOT on page refresh or navigation.
 * Uses form-encoded body to avoid CORS preflight issues with sendBeacon.
 */
function sendGoodbye() {
  const token = sessionStorage.getItem(SESSION_KEY);
  if (!token) return;

  const body = `session_token=${encodeURIComponent(token)}`;

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/x-www-form-urlencoded" });
    navigator.sendBeacon(`${API_BASE}/visitor_goodbye`, blob);
  }

  // Clear token so a fresh session starts after re-login
  sessionStorage.removeItem(SESSION_KEY);
}

export { sendGoodbye };

export default function useVisitorHeartbeat() {
  const intervalRef = useRef(null);

  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        const sessionToken = getSessionToken();
        const pagePath = window.location.pathname;

        await baseURL.post("/visitor_heartbeat", {
          session_token: sessionToken,
          page_path: pagePath,
        });
      } catch {
        // Silently ignore errors — don't break the app
      }
    };

    // Send immediately on mount
    sendHeartbeat();

    // Then every 30 seconds
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // NOTE: No beforeunload listener here. On tab close, sessionStorage is
    // automatically cleared by the browser, and the backend session expires
    // after 1 minute of no heartbeats. This avoids the bug where a page
    // refresh would fire beforeunload → delete session → create a new one
    // → inflated visitor count.

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);
}
